import { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Key, Save, X, CheckSquare, Square, MinusSquare, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";

// Tree of main menus → sub-menu items, mapped to the flat backend MENU_KEYS.
const TREE = [
  { label: "Dashboard", leaf: "dashboard" },
  { label: "CRM", children: [["customer_master", "Customer Master"], ["feedback", "Feedback"], ["campaigns", "Marketing Campaigns"]] },
  { label: "Transactions", leaf: "transactions" },
  { label: "Workshop", children: [["bookings", "Booking"], ["vehicles", "Vehicle"], ["jobcards", "Job Card"], ["invoices", "Invoices"], ["jobrates", "Rate Card"], ["packages", "Package"], ["reminders", "Reminders"]] },
  { label: "Inventory", children: [["items", "Item Master"], ["companies", "Company Master"], ["categories", "Category Master"], ["groups", "Group Master"], ["rates", "Rate Master"], ["supplier_companies", "Supplier Wise Company"], ["hsn", "HSN Code Master"], ["labels", "Labels"]] },
  { label: "Stocks", leaf: "stocks" },
  { label: "Accounts", children: [["day_book", "Day Book"], ["cash_book", "Cash Book"], ["bank_book", "Bank Book"], ["customer_ledger", "Customer Ledger"], ["vendor_ledger", "Ledger"], ["expenses", "Expenses"], ["vouchers", "Vouchers"], ["gst_reports", "GST Reports"], ["profit_loss", "Profit & Loss"]] },
  { label: "AI Insights", leaf: "insights" },
  { label: "HRMS", leaf: "hrms" },
  { label: "Reports", leaf: "reports" },
  { label: "Org & Access", children: [["organisation", "Organisation Master"], ["users", "User Master"], ["roles", "Role Master"], ["permissions", "Permission Master"]] },
  { label: "Customers (legacy)", leaf: "customers" },
];

// UI shows View / Edit / Delete per role. "Edit" maps to backend add+edit together.
const COLUMNS = [
  { key: "view", label: "View", actions: ["view"] },
  { key: "edit", label: "Edit", actions: ["add", "edit"] },
  { key: "delete", label: "Delete", actions: ["delete"] },
];

const cellState = (row, col) => col.actions.every(a => row?.[a]);

const PermCell = ({ state, ind, testId, onToggle }) => (
  <td className="p-2 text-center">
    <button type="button" data-testid={testId} onClick={onToggle} className="p-1">
      {ind ? <MinusSquare className="w-4 h-4 text-primary/60"/>
        : state ? <CheckSquare className="w-4 h-4 text-primary"/>
        : <Square className="w-4 h-4 text-muted-foreground"/>}
    </button>
  </td>
);

export default function PermissionsMaster() {
  const [data, setData] = useState(null);
  const [roleFilter, setRoleFilter] = useState("all");
  const [perms, setPerms] = useState({}); // { role: { menuKey: {view,add,edit,delete} } }
  const [expanded, setExpanded] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/permissions").then(r => {
    setData(r.data);
    const filled = {};
    (r.data.permissions || []).forEach(p => {
      filled[p.role] = {};
      (r.data.menu_keys || []).forEach(m => {
        filled[p.role][m] = { view: false, add: false, edit: false, delete: false, ...(p.menus?.[m] || {}) };
      });
    });
    setPerms(filled);
    setDirty(false);
  }).catch(e => toast.error(formatErr(e)));
  useEffect(() => { load(); }, []);

  const roles = useMemo(() => (data?.permissions || []).map(p => p.role), [data]);
  const visibleRoles = useMemo(() => roleFilter === "all" ? roles : roles.filter(r => r === roleFilter), [roles, roleFilter]);

  const toggleLeaf = (role, menuKey, col) => {
    const cur = perms[role]?.[menuKey] || {};
    const val = !col.actions.every(a => cur[a]);
    setPerms(p => ({ ...p, [role]: { ...p[role], [menuKey]: { ...cur, ...Object.fromEntries(col.actions.map(a => [a, val])) } } }));
    setDirty(true);
  };

  const toggleParent = (role, children, col) => {
    const allOn = children.every(([k]) => col.actions.every(a => perms[role]?.[k]?.[a]));
    const val = !allOn;
    setPerms(p => {
      const rp = { ...p[role] };
      children.forEach(([k]) => { rp[k] = { ...rp[k], ...Object.fromEntries(col.actions.map(a => [a, val])) }; });
      return { ...p, [role]: rp };
    });
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await Promise.all(visibleRoles.map(role =>
        api.put("/permissions", { role, menus: perms[role] })
      ));
      toast.success(roleFilter === "all" ? "Permissions saved for all roles" : `Permissions saved for ${roleFilter}`);
      setDirty(false); load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };

  const cancel = () => { load(); toast.info("Changes discarded"); };

  if (!data) return <div className="p-8 text-muted-foreground">Loading permissions…</div>;

  return (
    <div className="p-8">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-primary text-xs uppercase tracking-[0.3em] mb-2 flex items-center gap-2"><Key className="w-3 h-3"/> Owner only · View / Edit / Delete per menu</div>
          <h1 className="font-display font-black text-4xl uppercase tracking-tighter">Permission Master</h1>
          <div className="text-xs text-muted-foreground mt-1">Manage role permissions across all menus and sub-menus</div>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Role</Label>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger data-testid="perm-role-filter" className="mt-1 rounded-sm bg-secondary border-border w-56"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem data-testid="perm-role-opt-all" value="all">All Roles</SelectItem>
                {roles.map(r => <SelectItem key={r} data-testid={`perm-role-opt-${r}`} value={r}>{r.toUpperCase()}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="perm-cancel-btn" variant="outline" onClick={cancel} disabled={!dirty || saving} className="rounded-sm text-xs uppercase tracking-wider h-10"><X className="w-4 h-4 mr-1"/> Cancel</Button>
          <Button data-testid="perm-save-btn" onClick={save} disabled={!dirty || saving} className="rounded-sm bg-primary text-primary-foreground uppercase tracking-[0.15em] text-xs h-10"><Save className="w-4 h-4 mr-1"/> Save</Button>
        </div>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-hidden overflow-x-auto">
        <table className="w-full text-sm" data-testid="perm-matrix">
          <thead className="bg-secondary text-[10px] uppercase tracking-[0.2em] text-muted-foreground sticky top-0 z-10">
            <tr>
              <th className="text-left p-3 sticky left-0 bg-secondary min-w-56">Menu / Sub-Item</th>
              {visibleRoles.map(r => (
                <th key={r} colSpan={3} className="text-center p-3 border-l border-border text-primary" data-testid={`perm-rolehead-${r}`}>{r.toUpperCase()}</th>
              ))}
            </tr>
            <tr>
              <th className="text-left p-2 sticky left-0 bg-secondary"></th>
              {visibleRoles.map(r => COLUMNS.map(c => (
                <th key={`${r}-${c.key}`} className="text-center p-2 w-16 border-l border-border/50 first:border-l-0">{c.label}</th>
              )))}
            </tr>
          </thead>
          <tbody>
            {TREE.map(node => {
              if (node.leaf) {
                return (
                  <tr key={node.label} data-testid={`perm-row-${node.leaf}`} className="border-t border-border hover:bg-secondary/40">
                    <td className="p-3 text-xs font-semibold sticky left-0 bg-card">{node.label}</td>
                    {visibleRoles.map(r => COLUMNS.map(c => (
                      <PermCell key={`${r}-${c.key}`} state={cellState(perms[r]?.[node.leaf], c)} testId={`cell-${r}-${node.leaf}-${c.key}`} onToggle={() => toggleLeaf(r, node.leaf, c)}/>
                    )))}
                  </tr>
                );
              }
              const open = expanded[node.label] !== false; // default expanded
              const slug = node.label.toLowerCase().replace(/[^a-z0-9]+/g, "-");
              return [
                <tr key={node.label} data-testid={`perm-parent-${slug}`} className="border-t border-border bg-secondary/30">
                  <td className="p-3 text-xs font-bold uppercase tracking-wider sticky left-0 bg-secondary/30">
                    <button type="button" data-testid={`perm-expand-${slug}`} onClick={() => setExpanded(x => ({ ...x, [node.label]: !open }))} className="flex items-center gap-1.5">
                      {open ? <ChevronDown className="w-3.5 h-3.5"/> : <ChevronRight className="w-3.5 h-3.5"/>}
                      {node.label}
                    </button>
                  </td>
                  {visibleRoles.map(r => COLUMNS.map(c => {
                    const vals = node.children.map(([k]) => c.actions.every(a => perms[r]?.[k]?.[a]));
                    const allOn = vals.every(Boolean);
                    const someOn = vals.some(Boolean);
                    return <PermCell key={`${r}-${c.key}`} state={allOn} ind={!allOn && someOn} testId={`cell-${r}-${slug}-${c.key}`} onToggle={() => toggleParent(r, node.children, c)}/>;
                  }))}
                </tr>,
                ...(open ? node.children.map(([k, label]) => (
                  <tr key={k} data-testid={`perm-row-${k}`} className="border-t border-border/60 hover:bg-secondary/40">
                    <td className="p-3 pl-9 text-xs text-muted-foreground sticky left-0 bg-card">{label}</td>
                    {visibleRoles.map(r => COLUMNS.map(c => (
                      <PermCell key={`${r}-${c.key}`} state={cellState(perms[r]?.[k], c)} testId={`cell-${r}-${k}-${c.key}`} onToggle={() => toggleLeaf(r, k, c)}/>
                    )))}
                  </tr>
                )) : []),
              ];
            })}
          </tbody>
        </table>
      </div>
      <div className="text-xs text-muted-foreground mt-3">Checking a main menu applies to all its sub-menu items; unchecking any sub-item clears the parent. Permissions drive nav visibility & action buttons across the app. Backend role gates remain in place as a safety net.</div>
    </div>
  );
}
